<script setup lang="ts">
/**
 * The team's shopping list, and the log of what was bought when.
 *
 * Its own view, not a kind of board: an item is never finished, only bought
 * and needed again, and "when did we last get this" is the question a task's
 * status cannot answer (server/shopping.ts).
 *
 * The box at the top both finds and adds. Typing narrows the list; Enter puts
 * the item it names on the list, adding it first if there is no such item —
 * so "milk ⏎" does the right thing whether or not milk has been bought before.
 *
 * Opening an item sets its price and store. "Bought" logs a purchase at that
 * price, and each purchase keeps its own copy, so the totals add up what was
 * actually paid — a price change later never rewrites last month. A purchase
 * whose price was wrong is corrected in the item's history, on its own.
 */
import { computed, ref, watch } from "vue";
import { useBoard } from "../stores/board.js";
import { useShopping } from "../stores/shopping.js";
import { vSynced } from "../lib/syncedValue.js";
import { nowMs } from "../lib/clock.js";
import { startOfDay } from "../lib/calendar.js";
import { lastBought } from "../lib/format.js";
import { toast } from "../lib/toast.js";
import { amountText, formatAmount, parseAmount } from "@shared/finance.js";
import { SHOPPING_NAME_MAX, SHOPPING_STORE_MAX, type Purchase, type ShoppingItem } from "@shared/types.js";

const board = useBoard();
const shop = useShopping();

watch(
  () => board.teamId,
  (team) => {
    if (team) void shop.load(team).catch((err) => toast(message(err)));
  },
  { immediate: true },
);

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

async function attempt(what: () => Promise<unknown>): Promise<void> {
  try {
    await what();
  } catch (err) {
    toast(message(err));
  }
}

// ── money ──────────────────────────────────────────────────────────────────
// A team list has no currency of its own (the ledger's is per person), so a
// price is a plain amount in hundredths.
const money = (minor: number): string => formatAmount(minor, null);
const priceInput = (minor: number | null): string => (minor === null ? "" : amountText(minor, 2));

/** What was typed as a price: null for blank, undefined when it is not one. */
function readPrice(text: string): number | null | undefined {
  const t = text.trim();
  if (!t) return null;
  // parseAmount refuses zero, which is a ledger rule; a free sample is a price.
  if (/^\D*0*\.?0*$/.test(t) && /0/.test(t)) return 0;
  return parseAmount(t, 2) ?? undefined;
}

/** What the priced purchases in a list add up to, and whether any were unpriced. */
function total(list: Purchase[]): { sum: number; priced: number } {
  let sum = 0;
  let priced = 0;
  for (const p of list) {
    if (p.priceMinor === null) continue;
    sum += p.priceMinor;
    priced++;
  }
  return { sum, priced };
}

// ── find / add ─────────────────────────────────────────────────────────────
const query = ref("");
const wanted = computed(() => query.value.trim().replace(/\s+/g, " "));
const matches = (item: ShoppingItem): boolean =>
  !wanted.value || item.name.toLowerCase().includes(wanted.value.toLowerCase());
const needed = computed(() => shop.needed.filter(matches));
const stocked = computed(() => shop.stocked.filter(matches));
/**
 * Both lists as ONE keyed run, the "Got" heading between them. An item
 * somebody buys, or puts back on the list, moves across; in two separate
 * lists it would be torn down and rebuilt, and the open one would lose
 * whatever was half-typed in it.
 */
const listed = computed((): { key: string; item: ShoppingItem | null }[] => [
  ...needed.value.map((item) => ({ key: item.id, item })),
  ...(stocked.value.length || !wanted.value ? [{ key: " got", item: null }] : []),
  ...stocked.value.map((item) => ({ key: item.id, item })),
]);
const exact = computed(() =>
  [...shop.items.values()].find((i) => i.name.toLowerCase() === wanted.value.toLowerCase()) ?? null,
);

async function submit(): Promise<void> {
  if (!wanted.value) return;
  const name = wanted.value;
  await attempt(async () => {
    const found = exact.value;
    if (found) {
      if (!found.needed) await shop.update(found.id, { needed: true });
    } else {
      await shop.add(name);
    }
    query.value = "";
  });
}

// ── one item, opened ──────────────────────────────────────────────────────
const openId = ref<string | null>(null);
const open = computed(() => (openId.value ? (shop.items.get(openId.value) ?? null) : null));
/** Its whole history, fetched when opened: the team log only holds the newest part. */
const history = ref<Purchase[]>([]);
const deleteArmed = ref(false);

function toggleOpen(item: ShoppingItem): void {
  openId.value = openId.value === item.id ? null : item.id;
}

watch(openId, (id) => {
  history.value = [];
  deleteArmed.value = false;
  editing.value = null;
  if (id) void loadHistory(id);
});

// The name, price and store boxes show what the item says now — someone
// else's change included — through `v-synced`, which never writes into a box
// somebody is typing in. So their text is read off the box, not kept here.

async function loadHistory(id: string): Promise<void> {
  try {
    const list = await shop.history(id);
    if (openId.value === id) history.value = list;
  } catch (err) {
    toast(message(err));
  }
}

// Keep the open item's history in step with the log as purchases come and go.
watch(
  () => open.value?.timesBought,
  () => {
    if (openId.value) void loadHistory(openId.value);
  },
);

async function saveName(box: HTMLInputElement | null): Promise<void> {
  const item = open.value;
  const name = (box?.value ?? "").trim();
  if (!item || !name || name === item.name) return;
  await attempt(() => shop.update(item.id, { name }));
}

async function savePrice(box: HTMLInputElement): Promise<void> {
  const item = open.value;
  if (!item) return;
  const price = readPrice(box.value);
  if (price === undefined) {
    toast(`${box.value.trim()} is not a price`);
    box.value = priceInput(item.priceMinor);
    return;
  }
  if (price === item.priceMinor) {
    box.value = priceInput(price);
    return;
  }
  await attempt(() => shop.update(item.id, { priceMinor: price }));
}

async function saveStore(box: HTMLInputElement): Promise<void> {
  const item = open.value;
  const store = box.value.trim().replace(/\s+/g, " ");
  if (!item || store === item.store) return;
  await attempt(() => shop.update(item.id, { store }));
}

/** The open item's history, with any purchase the socket has since corrected. */
const historyShown = computed(() => history.value.map((p) => shop.purchases.get(p.id) ?? p));
const spent = computed(() => total(historyShown.value));

/** Up or down against the purchase before it — the history is newest first. */
function trend(i: number): "up" | "down" | null {
  const now = historyShown.value[i]?.priceMinor ?? null;
  const before = historyShown.value.slice(i + 1).find((p) => p.priceMinor !== null)?.priceMinor ?? null;
  if (now === null || before === null || now === before) return null;
  return now > before ? "up" : "down";
}

// ── correcting one purchase ──
const editing = ref<string | null>(null);
const editPrice = ref("");
const editStore = ref("");

function startEdit(p: Purchase): void {
  editing.value = p.id;
  editPrice.value = priceInput(p.priceMinor);
  editStore.value = p.store;
}

async function saveEdit(p: Purchase): Promise<void> {
  const price = readPrice(editPrice.value);
  if (price === undefined) {
    toast(`${editPrice.value.trim()} is not a price`);
    return;
  }
  const store = editStore.value.trim().replace(/\s+/g, " ");
  await attempt(async () => {
    const saved = await shop.updatePurchase(p.id, { priceMinor: price, store });
    history.value = history.value.map((h) => (h.id === saved.id ? saved : h));
    editing.value = null;
  });
}

async function removeOpen(): Promise<void> {
  const item = open.value;
  if (!item) return;
  if (!deleteArmed.value) {
    deleteArmed.value = true;
    return;
  }
  await attempt(async () => {
    await shop.remove(item.id);
    openId.value = null;
  });
}

// ── the log ────────────────────────────────────────────────────────────────
const dayLabel = (ms: number): string =>
  new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(ms);
const timeLabel = (ms: number): string => new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(ms);
const fullLabel = (ms: number): string => `${dayLabel(ms)}, ${timeLabel(ms)}`;

/** The log a day at a time, newest first, each with what it cost. */
const days = computed(() => {
  const out: { key: number; label: string; entries: Purchase[]; spent: { sum: number; priced: number } }[] = [];
  for (const p of shop.log) {
    const key = startOfDay(p.boughtAt);
    const last = out[out.length - 1];
    if (last && last.key === key) last.entries.push(p);
    else out.push({ key, label: dayLabel(p.boughtAt), entries: [p], spent: { sum: 0, priced: 0 } });
  }
  for (const day of out) day.spent = total(day.entries);
  return out;
});

const itemName = (id: string): string => shop.items.get(id)?.name ?? "(removed)";

/** A mistaken "bought" comes out of the log on a second click. */
const armedPurchase = ref<string | null>(null);
async function removePurchase(id: string): Promise<void> {
  if (armedPurchase.value !== id) {
    armedPurchase.value = id;
    return;
  }
  armedPurchase.value = null;
  await attempt(() => shop.removePurchase(id));
}
</script>

<template>
  <section class="money shop-view" aria-label="Shopping list">
    <div class="money-inner">
      <form class="shop-find" @submit.prevent="submit">
        <input
          v-model="query"
          class="shop-find-input"
          :maxlength="SHOPPING_NAME_MAX"
          placeholder="Add or find an item…"
          aria-label="Add or find an item"
          autocomplete="off"
          enterkeyhint="done"
        />
        <button class="btn primary" type="submit" :disabled="!wanted">
          {{ exact ? (exact.needed ? "On the list" : "Need to buy") : "Add" }}
        </button>
      </form>

      <div v-if="!shop.loaded" class="empty">…</div>

      <div v-else class="shop-cols">
        <div class="money-card shop-list">
          <h3>To buy <span class="shop-count">{{ shop.needed.length }}</span></h3>
          <p v-if="!needed.length" class="money-hint">
            {{ wanted ? "Nothing on the list matches." : "Nothing needed right now." }}
          </p>
          <template v-for="{ key, item } in listed" :key="key">
            <h3 v-if="!item" class="shop-sub">
              Got <span class="shop-count">{{ shop.stocked.length }}</span>
            </h3>
            <div v-else class="shop-item" :class="{ needed: item.needed, open: openId === item.id }">
              <div class="shop-item-row">
                <button class="shop-name" type="button" :title="`Show ${item.name}'s history`" @click="toggleOpen(item)">
                  {{ item.name }}
                </button>
                <span class="shop-last" :title="item.lastBoughtAt ? fullLabel(item.lastBoughtAt) : 'never bought'">
                  <template v-if="item.priceMinor !== null"><b class="shop-price">{{ money(item.priceMinor) }}</b> · </template>last bought: {{ lastBought(item.lastBoughtAt, nowMs) }}
                </span>
                <button
                  class="shop-btn need"
                  :class="{ on: item.needed }"
                  type="button"
                  :aria-pressed="item.needed"
                  :title="item.needed ? 'On the list — click to take it off' : 'Put it on the list'"
                  @click="attempt(() => shop.update(item.id, { needed: !item.needed }))"
                >Need to buy</button>
                <button
                  class="shop-btn bought"
                  type="button"
                  title="Log a purchase, now, and take it off the list"
                  @click="attempt(() => shop.buy(item.id))"
                >Bought</button>
              </div>

              <div v-if="openId === item.id && open" class="shop-detail">
                <form class="shop-rename" @submit.prevent="saveName(($event.target as HTMLFormElement).querySelector('input'))">
                  <input
                    v-synced="{ key: item.id, value: open.name }"
                    :maxlength="SHOPPING_NAME_MAX"
                    aria-label="Name"
                    @blur="saveName($event.target as HTMLInputElement)"
                  />
                  <button
                    class="btn"
                    :class="deleteArmed ? 'danger' : 'ghost'"
                    type="button"
                    :title="deleteArmed ? 'Click again to delete it and its history' : 'Delete (click twice)'"
                    @click="removeOpen"
                  >{{ deleteArmed ? "Delete?" : "Delete" }}</button>
                </form>
                <div class="shop-fields">
                  <label class="shop-field price">
                    <span>Price</span>
                    <input
                      v-synced="{ key: item.id, value: priceInput(open.priceMinor) }"
                      inputmode="decimal"
                      placeholder="0.00"
                      aria-label="Price"
                      @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
                      @blur="savePrice($event.target as HTMLInputElement)"
                    />
                  </label>
                  <label class="shop-field store">
                    <span>Store</span>
                    <input
                      v-synced="{ key: item.id, value: open.store }"
                      :maxlength="SHOPPING_STORE_MAX"
                      list="shop-stores"
                      placeholder="Where from?"
                      aria-label="Store"
                      autocomplete="off"
                      @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
                      @blur="saveStore($event.target as HTMLInputElement)"
                    />
                  </label>
                </div>
                <p class="money-hint">
                  Bought {{ open.timesBought }} time{{ open.timesBought === 1 ? "" : "s" }}<template v-if="spent.priced">,
                    {{ money(spent.sum) }} in all<template v-if="spent.priced < historyShown.length">
                      ({{ historyShown.length - spent.priced }} without a price)</template></template><template v-if="history.length">:</template>
                </p>
                <ul v-if="history.length" class="shop-history">
                  <li v-for="(p, i) in historyShown" :key="p.id">
                    <form v-if="editing === p.id" class="shop-edit" @submit.prevent="saveEdit(p)" @keydown.esc="editing = null">
                      <span class="shop-edit-when">{{ fullLabel(p.boughtAt) }}</span>
                      <input v-model="editPrice" inputmode="decimal" placeholder="0.00" aria-label="Price paid" />
                      <input v-model="editStore" :maxlength="SHOPPING_STORE_MAX" list="shop-stores" placeholder="Store" aria-label="Store" />
                      <button class="btn primary" type="submit">Save</button>
                      <button class="btn ghost" type="button" @click="editing = null">Cancel</button>
                    </form>
                    <template v-else>
                      {{ fullLabel(p.boughtAt) }} <span class="shop-who">· {{ board.userName(p.userId) }}</span>
                      <button
                        class="shop-paid"
                        type="button"
                        :title="'Correct what this one cost or where it was from'"
                        @click="startEdit(p)"
                      >
                        <template v-if="p.priceMinor !== null">
                          {{ money(p.priceMinor) }}<span v-if="trend(i)" class="shop-trend" :class="trend(i)!">{{ trend(i) === "up" ? "▲" : "▼" }}</span>
                        </template>
                        <template v-else>no price</template>
                        <span v-if="p.store" class="shop-who"> · {{ p.store }}</span>
                      </button>
                    </template>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </div>

        <div class="money-card shop-log">
          <h3>Bought</h3>
          <p v-if="!days.length" class="money-hint">Nothing bought yet. Every “Bought” is logged here.</p>
          <div v-for="day in days" :key="day.key" class="shop-day">
            <div class="shop-day-label">
              {{ day.label }}<span v-if="day.spent.priced" class="shop-day-total">{{ money(day.spent.sum) }}</span>
            </div>
            <div v-for="p in day.entries" :key="p.id" class="shop-entry">
              <span class="shop-time">{{ timeLabel(p.boughtAt) }}</span>
              <span class="shop-entry-name">{{ itemName(p.itemId) }}</span>
              <span v-if="p.priceMinor !== null" class="shop-entry-price" :title="p.store || undefined">{{ money(p.priceMinor) }}</span>
              <span class="shop-who">{{ board.userName(p.userId) }}</span>
              <button
                class="shop-undo"
                :class="{ armed: armedPurchase === p.id }"
                type="button"
                :title="armedPurchase === p.id ? 'Click again to take it out of the log' : 'Not bought after all? Take it out of the log'"
                :aria-label="`Remove ${itemName(p.itemId)} on ${fullLabel(p.boughtAt)} from the log`"
                @click="removePurchase(p.id)"
              >{{ armedPurchase === p.id ? "Remove?" : "×" }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <datalist id="shop-stores">
      <option v-for="store in shop.stores" :key="store" :value="store" />
    </datalist>
  </section>
</template>
